import type { ChangeEvent } from 'react';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { X, Search  } from 'react-feather';
import styled from '@emotion/styled';
import { useFloating } from '@floating-ui/react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { BiChatContextType, ChatContextType } from '@trinity/chat';
import { Portal } from '@trinity/chat-components/components/Portal';
import { DropdownMenu, DropdownMenuItem } from '@trinity/chat-components/components/presentation/DropdownMenu';
import { useSameWidthMiddleware, useZIndexMiddleware } from '@trinity/chat-components/components/presentation/popper-modifiers';
import { Constants } from '@trinity/chat-components/components/Theme';
import { toTitleCase, trunc } from '@trinity/chat-components/utils';
import { ContextSearchContext, useHeaderContext } from './context';
import type { ContextSearchResultItem } from './interfaces';

type Props = {
  className?: string;
};

export const ContextSearch = ({ className }: Props) => {
  const {
    handleItemSelected: onItemSelected,
    updateValue,
    value,
    results,
  } = useContext(ContextSearchContext);
  const {
    pendingContext,
    pendingContextType,
    cancelPendingSearch,
    pendingEverythingSearch,
  } = useHeaderContext();

  const [focused, setFocused] = useState(false);

  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  const zIndexMiddleware = useZIndexMiddleware();
  const sameWidthMiddleware = useSameWidthMiddleware(1.1);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [zIndexMiddleware, sameWidthMiddleware],
  });

  const popperVisible = useMemo(() => {
    if (!focused) return false;

    return !!results?.length;
  }, [
    focused,
    results,
  ]);

  const handleItemSelected = useCallback((item: ContextSearchResultItem) => (_: React.MouseEvent) => {
    onItemSelected(item);
    setFocused(false);
  }, [onItemSelected]);

  const handleBlur = useCallback((e: React.FocusEvent) => {
    if (dropdownMenuRef.current?.contains(e.relatedTarget)) {
      return;
    }
    setFocused(false);
  }, []);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    updateValue(e.target.value);
  }, [updateValue]);

  const handleClickAway = useCallback((e: MouseEvent) => {

    if (refs.domReference.current != (e.target as Node)) {
      setFocused(false);
    }
  }, [refs.domReference]);

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleCancel = useCallback(() => {
    cancelPendingSearch();
  }, [cancelPendingSearch]);

  const placeholder = useMemo(() => {
    if (!pendingContextType) return '';

    if (pendingContextType === ChatContextType.Agent
      && pendingContext.subjectAutocomplete) {

      const DisplayMap = {
        [BiChatContextType.Project]: 'Search Projects',
        [BiChatContextType.Folder]: 'Search Folders',
        [BiChatContextType.Brand]: 'Search Brands',
        [BiChatContextType.Document]: 'Search Documents',
        [BiChatContextType.Transcript]: 'Search Transcripts',
      };

      return DisplayMap[pendingContext.identifier] ?? '';
    }

    if (pendingContextType === ChatContextType.Global) {
      return 'Search Anything';
    }
    if (pendingContextType === ChatContextType.KolProfile) {
      return 'Search HCP Profiles';
    }

    return `Search ${toTitleCase(pendingContextType)}s`;
  }, [pendingContext, pendingContextType]);

  return (
    <>
      <div
        className={className}
        ref={refs.setReference}>
        <Input
          placeholder={placeholder}
          onBlur={handleBlur}
          onCancel={handleCancel}
          onChange={handleInputChange}
          onFocus={handleFocus}
          value={value} />
      </div>
      {popperVisible &&
        <Portal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}>
            <ClickAwayListener onClickAway={handleClickAway}>
              <DropdownMenu ref={dropdownMenuRef}>
                {results.map(result => (
                  <StyledDropdownMenuItem
                    key={result.entityId}
                    onClick={handleItemSelected(result)}>
                    {trunc(result.name, 75)}
                    {pendingEverythingSearch || pendingContext.identifier === BiChatContextType.Global && (
                      <DropdownItemSubText>{buildResultSubtitle(result)}</DropdownItemSubText>
                    )}
                  </StyledDropdownMenuItem>
                ))}
              </DropdownMenu>
            </ClickAwayListener>
          </div>
        </Portal>
      }
    </>
  );
};

function buildResultSubtitle(item: ContextSearchResultItem) {
  if (item.type === ChatContextType.KolProfile) {
    return 'HCP Profile';
  }

  if (item.type === BiChatContextType.Project) {
    return 'Project';
  }

  if (item.type === BiChatContextType.Folder) {
    return 'Folder';
  }

  if (item.type === BiChatContextType.Brand) {
    return 'Brand';
  }

  if (item.type === BiChatContextType.Document) {
    return 'Document';
  }

  if (item.type === BiChatContextType.Transcript) {
    return 'Transcript';
  }

  return toTitleCase(item.type);
}

type InputProps = {
  onCancel: () => void;
} & React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

function Input(props: InputProps) {

  return (
    <InputRoot>
      <InputLeft>
        <StyledSearchIcon size={IconSize} />
        <StyledInput {...props} autoFocus />
      </InputLeft>
      <StyledXIcon onClick={props.onCancel} size={IconSize} />
    </InputRoot>
  );
}

const IconSize = 19;
const InputMarginLeft = 6;
const RootHorizontalPadding = 8;

const InputRoot = styled.div(({ theme }) => ({
  boxSizing: 'border-box',
  color: theme.palette.black.main,
  backgroundColor: theme.palette.gray.light2,
  borderRadius: 4,
  height: Constants.Header.ItemHeight,
  width: '100%',
  maxWidth: 300,
  padding: `0 ${RootHorizontalPadding}px`,

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const InputLeft = styled.div({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
});

const StyledXIcon = styled(X)(({ theme }) => ({
  color: theme.palette.black.main,
  cursor: 'pointer',
  paddingLeft: 5,
  display: 'flex',
  flexShrink: 0,
}));

const StyledSearchIcon = styled(Search)(({ theme }) => ({
  color: theme.palette.black.main,
  display: 'flex',
  flexShrink: 0,
}));

const StyledInput = styled.input(({ theme }) => ({
  backgroundColor: 'transparent',
  border: 'none',
  fontSize: 14,
  lineHeight: 20,
  marginLeft: InputMarginLeft,
  textOverflow: 'ellipsis',
  transition: 'all 750ms ease',
  height: '100%',
  width: '100%',
  color: theme.palette.black.main,
  fontFamily: theme.fonts.regular,
}));

const StyledDropdownMenuItem = styled(DropdownMenuItem)(({ theme }) => ({
  display: 'block',
  fontFamily: theme.fonts.semiBold,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const DropdownItemSubText = styled.div(({ theme }) => ({
  fontSize: 12,
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.gray.main,
  marginTop: 3,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));